<script setup lang="ts">
import type { UploadId, UploadingFile } from '#layers/assets/stores/upload';
import { computed, ref, useId, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from '#app/composables/router';
import { useAppConfig } from '#app/config';
import { useFileUploadLimit } from '#layers/assets/composables/use-file-upload-limit';
import { UploadScope, useUploadStore } from '#layers/assets/stores/upload';
import { formatFilesize } from '#layers/assets/utils/format-filesize';
import { toCompositePrimaryKey, useFileCollection } from '#layers/data-model/public';
import { useCollectionRoutes } from '#layers/spaces/public';
import { usePinnedSurface } from '#layers/ui/public';
import { useWorkspaceStore } from '#layers/workspace/public';

interface UploadListItem extends UploadingFile {
	selectable: boolean;
	statusLabel: string | undefined;
}

const appConfig = useAppConfig();
const uploadStore = useUploadStore();
const workspaceStore = useWorkspaceStore();
const router = useRouter();
const { t } = useI18n();
const { maxFileUploadSizeBytes } = useFileUploadLimit();

const { collection: fileCollection } = useFileCollection();
const { detailRoute } = useCollectionRoutes(fileCollection);

const panelId = useId();
const isPanelExpanded = ref(false);

const hasActiveUploads = computed(() => uploadStore.allFiles.length > 0);

const uploadItems = computed<UploadListItem[]>(() => uploadStore.allFiles.map((upload) => ({
	...upload,
	selectable: canOpenUpload(upload),
	statusLabel: getFileStatusLabel(upload),
})));

const status = computed(() => {
	const uploading = uploadStore.uploadingFiles.length;
	const failed = uploadStore.failedFiles.length;
	const succeeded = uploadStore.successfulFiles.length;
	const aborted = uploadStore.abortedFiles.length;

	if (uploading > 0) return { summary: t('upload.n_uploading', { n: uploading }), icon: appConfig.ui.icons.loader, iconClass: 'animate-spin' };
	if (failed > 0) return { summary: t('upload.n_failed', { n: failed }), icon: 'lucide:circle-x', iconClass: 'text-error' };
	if (succeeded > 0) return { summary: t('upload.n_completed', { n: succeeded }), icon: 'lucide:circle-check', iconClass: 'text-success' };

	return { summary: t('upload.n_aborted', { n: aborted }), icon: 'lucide:circle-slash', iconClass: 'text-muted' };
});

const expandIcon = computed(() => (isPanelExpanded.value ? appConfig.ui.icons.chevronDown : appConfig.ui.icons.chevronUp));

const cardRef = useTemplateRef<HTMLElement>('card');
const { release: releasePinnedSurface } = usePinnedSurface(cardRef);

function getFileStatusIcon(status: UploadingFile['status']) {
	switch (status) {
		case 'success':
			return 'lucide:circle-check';
		case 'aborted':
			return 'lucide:circle-slash';
		case 'error':
			return 'lucide:circle-x';
		case 'uploading':
			return 'lucide:loader-circle';
		default:
			return '';
	}
}

function getFileStatusIconClass(status: UploadingFile['status']) {
	switch (status) {
		case 'uploading':
			return 'animate-spin';
		default:
			return '';
	}
}

function getFileStatusColorClass(status: UploadingFile['status']) {
	switch (status) {
		case 'success':
			return 'text-success';
		case 'error':
			return 'text-error';
		default:
			return 'text-muted';
	}
}

function getFileStatusLabel(upload: UploadingFile) {
	// A queued upload has nothing to report yet, and no `upload.status.pending` message exists.
	if (upload.status === 'pending') return;

	// A failure on a file larger than the server limit was rejected for its size.
	if (upload.status === 'error' && isOverSizeLimit(upload.file)) return t('upload.too_large');

	return t(`upload.status.${upload.status}`);
}

function isOverSizeLimit(file: File) {
	return maxFileUploadSizeBytes.value !== null && file.size > maxFileUploadSizeBytes.value;
}

function canRemoveFile(status: UploadingFile['status']) {
	return status === 'error' || status === 'success' || status === 'aborted';
}

function canAbortUpload(status: UploadingFile['status']) {
	return status === 'pending' || status === 'uploading';
}

function canOpenUpload(upload: UploadingFile): upload is UploadingFile & { uploadedFileId: string } {
	return upload.status === 'success'
		&& upload.scope === UploadScope.Workspace
		&& !!upload.uploadedFileId
		&& upload.workspaceSlug === workspaceStore.currentWorkspaceSlug
		&& !!fileCollection.value;
}

function openUpload(upload: UploadListItem) {
	const collection = fileCollection.value;

	if (!collection || !canOpenUpload(upload)) return;

	const route = detailRoute(toCompositePrimaryKey(upload.uploadedFileId, collection));

	if (!route) return;

	router.push(route);
}

function togglePanel() {
	isPanelExpanded.value = !isPanelExpanded.value;
}

function clearCompleted() {
	uploadStore.clearCompleted();

	if (!hasActiveUploads.value) {
		isPanelExpanded.value = false;
	}
}

function removeFile(id: UploadId) {
	uploadStore.removeFile(id);
}

function abortUpload(id: UploadId) {
	uploadStore.abortUpload(id);
}

function releasePinnedSurfaceWhenGone() {
	if (!hasActiveUploads.value) releasePinnedSurface();
}
</script>

<template>
	<Transition
		enter-active-class="transition-all duration-200"
		enter-from-class="translate-y-full opacity-0"
		enter-to-class="translate-y-0 opacity-100"
		leave-active-class="transition-all duration-200"
		leave-from-class="translate-y-0 opacity-100"
		leave-to-class="translate-y-full opacity-0"
		@after-leave="releasePinnedSurfaceWhenGone"
	>
		<div
			v-if="hasActiveUploads"
			ref="card"
			class="
				fixed inset-e-4 bottom-4 z-50 w-[calc(100%-2rem)] overflow-hidden rounded-md border bg-default shadow-lg
				sm:w-96
			"
		>
			<DButton
				block
				variant="ghost"
				color="neutral"
				:icon="status.icon"
				:trailing-icon="expandIcon"
				:aria-expanded="isPanelExpanded"
				:aria-controls="panelId"
				:ui="{
					base: 'gap-2.5 rounded-none p-3',
					leadingIcon: status.iconClass,
					trailingIcon: 'size-4',
				}"
				@click="togglePanel"
			>
				<template #default="{ ui }">
					<span role="status" :class="ui.label({ class: 'flex-1 text-start font-medium text-highlighted' })">
						{{ status.summary }}
					</span>
				</template>
			</DButton>

			<DAnimatedSheet dimension="height" :duration="200">
				<div v-if="isPanelExpanded" :id="panelId" class="border-t">
					<DList
						:items="uploadItems"
						selectable
						class="max-h-80 overflow-y-auto border-none"
						:ui="{ item:
							{
								base: 'px-3',
								textWrapper: 'gap-0.5',
								label: 'text-xs',
								description: 'text-xs text-dimmed',
								trailing: `ms-4`,
							},
						}"
						@select="openUpload"
					>
						<template #item-leading="{ item: upload }">
							<FileTypeIcon
								class="h-8 shrink-0"
								:file="upload.file"
							/>
						</template>

						<template #item-label="{ item: upload }">
							{{ upload.file.name }}
						</template>

						<template #item-description="{ item: upload }">
							<span class="inline-flex items-center gap-2">
								<span>{{ formatFilesize(upload.file.size) }}</span>
								<DSeparator orientation="vertical" class="h-3" />
								<span class="inline-flex items-center gap-1" :class="getFileStatusColorClass(upload.status)">
									<DIcon
										class="shrink-0"
										size="12"
										:name="getFileStatusIcon(upload.status)"
										:class="getFileStatusIconClass(upload.status)"
									/>
									<DTooltip
										v-if="upload.status === 'error' && upload.error"
										:text="upload.error"
									>
										<span data-testid="upload-error-label" class="font-medium">{{ upload.statusLabel }}</span>
									</DTooltip>
									<span v-else-if="upload.statusLabel" class="font-medium">
										{{ upload.statusLabel }}
									</span>
								</span>
							</span>
						</template>

						<template #item-trailing="{ item: file }">
							<DTooltip
								v-if="canRemoveFile(file.status)"
								:text="$t('common.clear')"
							>
								<DButton
									variant="ghost"
									color="neutral"
									icon="lucide:x"
									size="xs"
									@click.stop="removeFile(file.id)"
								/>
							</DTooltip>
							<DTooltip
								v-if="canAbortUpload(file.status)"
								:text="$t('common.abort')"
							>
								<DButton
									variant="ghost"
									color="neutral"
									icon="lucide:x"
									size="xs"
									@click.stop="abortUpload(file.id)"
								/>
							</DTooltip>
						</template>
					</DList>

					<div
						v-if="uploadStore.completedFiles.length > 0"
						class="flex items-center justify-end border-t px-3 py-1.5"
					>
						<DButton
							variant="ghost"
							color="neutral"
							:label="$t('common.clear')"
							size="sm"
							@click="clearCompleted"
						/>
					</div>
				</div>
			</DAnimatedSheet>
		</div>
	</Transition>
</template>
